import { useEffect, useState, useMemo } from "react";
import {
  Plus,
  Calendar,
  Ticket,
  Wallet,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";

import StatCard from "./components/StatCard";
import { StatSkeleton } from "../../components/Skeleton";
import { AdminEventAPI } from "../../services/adminEvent.api";
import { AdminBookingAPI } from "../../services/adminBooking.api";
import {socket} from "../../utils/socket";
import { formatCurrencyCompact } from "../../utils/number";
import moment from "moment";

export default function Dashboard() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [latestBookings, setLatestBookings] = useState([]);

  /* ================= INITIAL FETCH ================= */
  async function fetchDashboard() {
    try {
      setLoading(true);

      const [eventRes, bookingRes, latestRes] =
        await Promise.all([
          AdminEventAPI.stats(),
          AdminBookingAPI.stats(),
          AdminBookingAPI.list({ limit: 5 }),
        ]);

      const eventData = eventRes.data || {}; // ✅ FIX
      const bookingData = bookingRes.data?.data || {}; // tetap pakai .data karena booking pakai successResponse

      setStats({
        events: eventData,
        bookings: bookingData,
      });

      setLatestBookings(
        latestRes.data?.data?.items || []
      );

    } catch (err) {
      console.error("Dashboard load error", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchDashboard();
  }, []);

  /* ================= WEBSOCKET ================= */
  useEffect(() => {
    if (!stats) return;

    /* NEW BOOKING → PENDING count +1 */
    socket.on("booking:created", (booking) => {
      setStats((prev) => ({
        ...prev,
        bookings: {
          ...prev.bookings,
          totalBookings:
            (prev.bookings.totalBookings || 0) + 1,
          pending:
            (prev.bookings.pending || 0) + 1,
        },
      }));

      setLatestBookings((prev) => [
        booking,
        ...prev.slice(0, 4),
      ]);
    });

    /* BOOKING UPDATED (covers: QRIS webhook, admin verify, manual verify)
       → paid +1, pending -1 */
    socket.on(
      "booking:updated",
      ({ bookingId, status, amount }) => {
        if (status !== "PAID") return;

        setStats((prev) => ({
          ...prev,
          bookings: {
            ...prev.bookings,
            paid: (prev.bookings.paid || 0) + 1,
            pending: Math.max(
              (prev.bookings.pending || 0) - 1,
              0
            ),
            revenueMonth:
              (prev.bookings.revenueMonth || 0) +
              (amount || 0),
            revenueAllTime:
              (prev.bookings.revenueAllTime || 0) +
              (amount || 0),
          },
        }));

        setLatestBookings((prev) =>
          prev.map((b) =>
            b._id === bookingId
              ? { ...b, paymentStatus: "PAID" }
              : b
          )
        );
      }
    );

    /* BOOKING VERIFIED (from Yukk webhook) — only for non-QRIS manual payments
       that use a separate verification path (adminVerifyBooking emits booking:updated instead) */
    socket.on(
      "booking:verified",
      ({ bookingId }) => {
        setStats((prev) => ({
          ...prev,
          bookings: {
            ...prev.bookings,
            paid: (prev.bookings.paid || 0) + 1,
            pending: Math.max(
              (prev.bookings.pending || 0) - 1,
              0
            ),
          },
        }));

        setLatestBookings((prev) =>
          prev.map((b) =>
            b._id === bookingId
              ? { ...b, paymentStatus: "PAID" }
              : b
          )
        );
      }
    );

    /* BOOKING EXPIRED → pending -1 */
    socket.on(
      "booking:expired",
      ({ bookingId }) => {
        setStats((prev) => ({
          ...prev,
          bookings: {
            ...prev.bookings,
            pending: Math.max(
              (prev.bookings.pending || 0) - 1,
              0
            ),
          },
        }));

        setLatestBookings((prev) =>
          prev.filter((b) => b._id !== bookingId)
        );
      }
    );

    /* ADMIN STATS UPDATE (from emitAdminStats — cron job, verify, expire)
       Full stats recalculation from backend → replace all booking stats */
    socket.on(
      "admin:booking-stats-updated",
      (data) => {
        setStats((prev) => ({
          ...prev,
          bookings: {
            ...prev.bookings,
            ...data,
          },
        }));
      }
    );

    return () => {
      socket.off("booking:created");
      socket.off("booking:updated");
      socket.off("booking:verified");
      socket.off("booking:expired");
      socket.off("admin:booking-stats-updated");
    };
  }, [stats]);

  /* ================= SAFE GUARD ================= */
  if (loading || !stats) {
    return (
      <div className="dashboard-stats">
        <StatSkeleton />
        <StatSkeleton />
        <StatSkeleton />
      </div>
    );
  }

  const activeEvents =
    stats?.events?.activeEvents ?? 0;
  const upcomingEvent =
    stats?.events?.upcomingEvent ?? null;

  const totalBookings =
    stats?.bookings?.totalBookings ?? 0;
  const paid = stats?.bookings?.paid ?? 0;
  const pending = stats?.bookings?.pending ?? 0;

  const revenueMonth =
  Number(stats?.bookings?.revenueMonth || 0);

  const revenueAllTime =
  Number(stats?.bookings?.revenueAllTime || 0);

  const total =
    (upcomingEvent?.sold ?? 0) +
    (upcomingEvent?.remaining ?? 0);

  const percent =
    total > 0
      ? ((upcomingEvent?.sold ?? 0) /
          total) *
        100
      : 0;

  /* ================= RENDER ================= */
  return (
    <div className="admin-dashboard">
      {/* HEADER */}
      <div className="dashboard-header">
        <div>
          <h1>Dashboard</h1>
          <p>Live system summary</p>
        </div>

        {/* <div className="flex gap-2">
          <button
            className="btn btn-secondary"
            onClick={() =>
              navigate("/admin/events")
            }
          >
            View Events
          </button>

          <button
            className="btn btn-primary"
            onClick={() =>
              navigate("/admin/events/new")
            }
          >
            <Plus size={16} />
            Create Event
          </button>
        </div> */}
      </div>

      {/* STATS */}
      <div className="dashboard-stats">
        <StatCard
          label="Active Events"
          value={activeEvents}
          icon={<Calendar />}
          variant="info"
        />

        <StatCard
          label="Total Bookings"
          value={totalBookings}
          icon={<Ticket />}
          variant="default"
        />

        <StatCard
          label="Pending Bookings"
          value={pending}
          icon={<AlertTriangle />}
          variant="warning"
        />
        <StatCard
          label="Revenue (All Time)"
          value={formatCurrencyCompact(revenueAllTime)}
          subValue={`Bulan ini: Rp ${revenueMonth.toLocaleString("id-ID")}`}
          icon={<Wallet />}
          variant="success"
        />
      </div>

      {/* ATTENTION */}
      {/* <div className="dashboard-card danger">
        <div className="card-header">
          <h2>
            <AlertTriangle size={18} />
            Attention Needed
          </h2>
        </div>

        <div className="card-body grid grid-2">
          <button
            className="alert-action"
            onClick={() =>
              navigate(
                "/admin/bookings?paymentStatus=PENDING"
              )
            }
          >
            <strong>{waitingPayment}</strong>
            <span>Waiting Payment</span>
            <ArrowRight size={16} />
          </button>
        </div>
      </div> */}

      {/* UPCOMING EVENT */}
      {upcomingEvent && (
        <div className="dashboard-card">
          <div className="card-header">
            <h2>Upcoming Event</h2>
          </div>

          <div className="card-body">
            <h3>{upcomingEvent.title}</h3>
            <p>
              {new Date(
                upcomingEvent.date
              ).toLocaleDateString("id-ID")}{" "}
              · {upcomingEvent.location}
            </p>

            <div className="progress-wrapper">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{
                    width: `${percent}%`,
                  }}
                />
              </div>

              <div className="progress-info">
                <span>
                  {upcomingEvent.sold ?? 0} sold
                </span>
                <span>
                  {upcomingEvent.remaining ?? 0} remaining
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* AKTIVITAS TERBARU */}
      <div className="dashboard-card">
        <div className="card-header">
          <h2>Aktivitas Terbaru</h2>
        </div>

        <div className="card-body">
          {latestBookings.length === 0 ? (
            <p className="muted">Belum ada booking</p>
          ) : (
            <ul className="booking-list">
              {latestBookings.map((b) => (
                <li
                  key={b._id}
                  onClick={() => navigate("/admin/bookings")}
                >
                  <div className="bl-info">
                    <strong>{b.customerSnapshot?.name}</strong>
                    <span>
                      {b.event?.title}
                      {b.ticket?.tierName ? ` · ${b.ticket.tierName}` : ""}
                      {b.ticket?.quantity ? ` ×${b.ticket.quantity}` : ""}
                    </span>
                  </div>

                  <div className="bl-meta">
                    <span className="bl-amount">
                      {formatCurrencyCompact(b.ticket?.total ?? 0)}
                    </span>
                    <div className="bl-bottom">
                      <span className="bl-time">
                        {moment(b.createdAt).fromNow()}
                      </span>
                      <span className={`status ${b.paymentStatus?.toLowerCase()}`}>
                        {b.paymentStatus}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
